import { SwDerivedApiStore } from "@sourcewhale/shared/src/svelte/api-stores/sw-api-store";
import { derived, writable } from "svelte/store";
import { getDashboardToDos } from "../../../api/dashboard";
import { todosModalItemStore } from "../../modals/TodosModal/stores";
import { selectedCampaignType } from "./utils/campaignTypes";
import {
  checkedToDos,
  completedToDoIds,
  filteredToDoActivityOptions,
  selectedToDosActivityOptions,
  selectedToDosCampaignOptions,
  selectedToDosDate,
  toDosCampaignOptions,
  toDosRefreshButtonState,
  toDosTodayDate,
} from "./utils/stores";
import { selectedToDosUser, todoActivityOptions } from "./utils/toDos";
import { saveToLocalStorage } from "./utils/utils";

const dashboardTodosArg = derived(
  [selectedCampaignType, selectedToDosUser],
  ([$selectedCampaignType, $selectedToDosUser]) =>
    $selectedCampaignType?.value && $selectedToDosUser?.value
      ? [
          {
            campaignType: $selectedCampaignType.value,
            userId: $selectedToDosUser.value,
          },
        ]
      : null,
);

export const dashboardTodosStore = SwDerivedApiStore({
  get: getDashboardToDos,
  argsStore: dashboardTodosArg,
  // onMessage: ({ message, update }) => {
  //   if (message.kind === "update-to-dos") {
  //     message.changes.forEach((change) => {
  //       update((dashboardTodos) => {
  //         Object.entries(dashboardTodos.toDosByDate).forEach(([date, data]) => {
  //           data.toDos.forEach((toDo, i) => {
  //             if (change.ids.includes(toDo.toDoId)) {
  //               dashboardTodos.toDosByDate[date].toDos[i] = {
  //                 ...toDo,
  //                 ...change.update,
  //               };
  //             }
  //           });
  //         });
  //         return dashboardTodos;
  //       });
  //       if (update.status === "done") {
  //         completedToDoIds.update(($completedToDoIds) => {
  //           const newCompletedToDoIds = [...$completedToDoIds];
  //           change.ids.forEach((id) => {
  //             if (!newCompletedToDoIds.includes(id)) {
  //               newCompletedToDoIds.push(id);
  //             }
  //           });
  //           return newCompletedToDoIds;
  //         });
  //       }
  //     });
  //   }
  // },
});

export const toDosByDate = derived(
  [dashboardTodosStore],
  ([$userToDosData]) => $userToDosData.toDosByDate || {},
);

const filteredToDosByDateWithCounts = derived(
  [
    selectedToDosActivityOptions,
    selectedToDosCampaignOptions,
    toDosByDate,
    completedToDoIds,
    toDosTodayDate,
  ],
  ([
    $selectedToDosActivityOptions,
    $selectedToDosCampaignOptions,
    $toDosByDate,
    $completedToDoIds,
    $toDosTodayDate,
  ]) => {
    const ret = {};
    const countsByDate = {};

    const kinds = $selectedToDosActivityOptions.map((option) => option.value);
    const campaignIds = $selectedToDosCampaignOptions.map(
      (option) => option.value,
    );
    Object.entries($toDosByDate).forEach(([date, data]) => {
      const toDos = [];

      const isToday = date === $toDosTodayDate;
      const counts = isToday
        ? { total: 0, completed: 0, overdue: 0, completedOverdue: 0 }
        : { total: 0, completed: 0 };

      data.toDos.forEach((toDo) => {
        if (
          kinds.includes(toDo.kind) &&
          campaignIds.includes(toDo.campaignId)
        ) {
          const completed = $completedToDoIds.includes(toDo.toDoId);

          counts.total += 1;
          if (completed) counts.completed += 1;
          else toDos.push(toDo);
          if (isToday && toDo.isOverdue) {
            counts.overdue += 1;
            if (completed) counts.completedOverdue += 1;
          }
        }
      });

      ret[date] = toDos;
      countsByDate[date] = counts;
    });

    return {
      filteredToDosByDate: ret,
      countsByDate,
    };
  },
);

const filteredToDosByDate = derived(
  [filteredToDosByDateWithCounts],
  ([$filteredToDosByDateWithCounts]) =>
    $filteredToDosByDateWithCounts.filteredToDosByDate,
);

export const toDosCountsByDate = derived(
  [filteredToDosByDateWithCounts],
  ([$filteredToDosByDateWithCounts]) =>
    $filteredToDosByDateWithCounts.countsByDate,
);

export const filterOverdue = writable(false);
export const overdueSort = writable(-1);

export const filteredToDos = derived(
  [filteredToDosByDate, selectedToDosDate, filterOverdue, overdueSort],
  ([
    $filteredToDosByDate,
    $selectedToDosDate,
    $filterOverdue,
    $overdueSort,
  ]) => {
    let filtered = $filteredToDosByDate[$selectedToDosDate] || [];

    if ($filterOverdue) {
      filtered = filtered.filter((todo) => todo.isOverdue);
      if ($overdueSort === 1) filtered.reverse();
    }
    return filtered;
  },
);

let initialized = false;

let contactModalOpen = false;

const OpenModal = () => {
  let $filteredToDos = [];

  let nextToDo = null;
  let prevToDo = null;
  let currentToDo = null;

  const set = (toDo, contactModalOnly = false) => {
    // We don't need to check other todos if we're only showing the contact modal
    if (contactModalOnly) {
      if (toDo) {
        todosModalItemStore.set({
          next: null,
          prev: null,
          ...toDo,
          toDoId: null, // Missing toDoId tells the modal to show the contact modal
          callback: () => {},
        });
        contactModalOpen = true;
      }
      return;
    }

    // Prevent below subscribe methods from closing the modal
    if (contactModalOpen) return;

    if (toDo && toDo.toDoId === currentToDo?.toDoId) return;
    currentToDo = toDo;
    if (!toDo) {
      todosModalItemStore.set(null);
      return;
    }
    const index = $filteredToDos.findIndex((t) => t.toDoId === toDo.toDoId);
    if (index === -1) {
      set(nextToDo);
      return;
    }
    const nextToDoIndex = index + 1;
    const prevToDoIndex = index - 1;
    nextToDo =
      nextToDoIndex < $filteredToDos.length
        ? $filteredToDos[nextToDoIndex]
        : null;
    prevToDo = prevToDoIndex > -1 ? $filteredToDos[prevToDoIndex] : null;

    const nextOrLoop = nextToDo ?? $filteredToDos[prevToDoIndex];

    const next = nextToDo ? () => set(nextToDo) : null;
    const prev = prevToDo ? () => set(prevToDo) : null;
    todosModalItemStore.set({
      next,
      prev,
      ...$filteredToDos[index],
      callback: (completedToDo) => {
        completedToDoIds.update(($completedToDoIds) => {
          if (!$completedToDoIds.includes(completedToDo.toDoId)) {
            $completedToDoIds.push(completedToDo.toDoId);
          }
          return $completedToDoIds;
        });
        if (nextOrLoop) set(nextOrLoop);
        else set(null); // close modal on last todo
      },
    });
  };

  filteredToDos.subscribe((x) => {
    $filteredToDos = x;
    set(currentToDo);
  });

  todosModalItemStore.subscribe((toDo) => {
    if (!toDo) {
      contactModalOpen = false;
      set(null);
    }
  });

  return {
    set,
  };
};
export const openTodosModal = OpenModal();

export const initDashboardStores = () => {
  if (initialized) return;
  initialized = true;

  dashboardTodosStore.subscribe((userToDosData) => {
    if (Object.keys(userToDosData).length === 0) return;
    checkedToDos.set([]);

    completedToDoIds.set([]);

    const newFilteredTodoActivityOptions = todoActivityOptions.filter(
      (opt) => opt.allUsers || userToDosData.toDoKinds.includes(opt.value),
    );

    filteredToDoActivityOptions.set(newFilteredTodoActivityOptions);

    selectedToDosActivityOptions.update(($selectedToDosActivityOptions) =>
      $selectedToDosActivityOptions.filter((option) =>
        newFilteredTodoActivityOptions
          .map((kind) => kind.value)
          .includes(option.value),
      ),
    );

    const today = Object.keys(userToDosData.toDosByDate).find(
      (date) => userToDosData.toDosByDate[date].dateTimeInfo.isToday,
    );
    toDosTodayDate.set(today);
    selectedToDosDate.set(today);

    const uniqueCampaignIds = new Set();
    const campaignOptions = [];

    Object.values(userToDosData.toDosByDate).forEach((date) => {
      date.toDos.forEach((toDo) => {
        if (!uniqueCampaignIds.has(toDo.campaignId)) {
          uniqueCampaignIds.add(toDo.campaignId);
          const label =
            toDo.campaignName.length > 58
              ? `${toDo.campaignName.slice(0, 56)}...`
              : toDo.campaignName;
          campaignOptions.push({
            value: toDo.campaignId,
            label,
            icon: "campaigns",
          });
        }
      });
    });

    toDosCampaignOptions.set(campaignOptions);

    selectedToDosCampaignOptions.update(($selectedToDosCampaignOptions) => {
      // looks to see if stored selected campaigns are valid for new To-Dos
      if (
        !$selectedToDosCampaignOptions.length ||
        !$selectedToDosCampaignOptions.every((option) =>
          uniqueCampaignIds.has(option.value),
        )
      ) {
        saveToLocalStorage("selectedToDosCampaignOptions", campaignOptions);
        return campaignOptions;
      }
      return $selectedToDosCampaignOptions;
    });
  });

  dashboardTodosStore.meta.subscribe((meta) => {
    // toDosRefreshButtonState should be split and handle hover states
    // separate from loading states, or be computed in the button
    // internally. But for now.

    if (!meta.fetching) {
      toDosRefreshButtonState.set("afterLoad");
    } else if (meta.firstFetchDone) {
      toDosRefreshButtonState.set("active");
    }
  });
};
